import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Link2, Link2Off, Search, ArrowRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { FELD_KLASSE } from '@/lib/feldKlassen';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { datumDe } from '@/lib/format';
import type { Bestellung, DetailZuordnung } from '@/types/detail';
import type { BestellungenResponse, BestellungZeile } from '@/types/dashboard';

/**
 * Eine Bestellung einer anderen zuordnen (A52, Befund #133).
 *
 * DER VORGANG IM LADEN
 *   Manche Ladenbestellungen müssen nachträglich in Shopify erfasst
 *   werden, weil BONUS die Steuerwerte fürs Ausland nicht eingeben
 *   lässt. Im Tracking steht die Bestellung dann schon als B-Nummer;
 *   sobald sie in Shopify entsteht, legt der Webhook sie ein zweites Mal
 *   an, als G-Nummer. Zwei Einträge im Brett für einen Stapel Bücher.
 *
 * ZWEI SEITEN, ZWEI ANSICHTEN
 *   An der SHOPIFY-Bestellung steht der Weg zur Zuordnung — dort fällt
 *   die Doppelung auf, dort liegt der frische Eintrag.
 *   An der übergeordneten steht der schreibgeschützte Abschnitt mit
 *   Nummer, Zustand, Versanddatum und Sendungsnummer. Ohne den hätten
 *   wir das Problem nur verschoben: Die Sendungsnummer verschwände mit
 *   der G-Nummer aus dem Blick, und genau deshalb wird sie ja nicht
 *   storniert.
 *
 * ES WIRD NICHTS VERSCHMOLZEN. Keine Positionen, keine Sendungen, keine
 * Notizen, keine Termine. Die zugeordnete Bestellung ist Beleg, nicht
 * Arbeitsvorrat — sie verschwindet nur aus dem Brett.
 */

/** Die Sätze zu den Absagen des Servers — je einer, den ein Mensch versteht. */
export function zuordnungFehlerText(fehler: string, fremdeNr?: string): string {
  switch (fehler) {
    case 'ziel_ist_quelle':
      return 'Eine Bestellung lässt sich nicht sich selbst zuordnen.';
    case 'ziel_ist_zugeordnet':
      return 'Diese Bestellung ist selbst schon einer anderen zugeordnet. Eine Kette aus Zuordnungen gibt es nicht — ordne stattdessen der obersten zu.';
    case 'ziel_traegt_zuordnung':
      return fremdeNr
        ? `Dieser Bestellung ist bereits ${fremdeNr} zugeordnet. Mehr als eine geht nicht — hebe die bestehende Zuordnung erst auf.`
        : 'Dieser Bestellung ist bereits eine andere zugeordnet. Mehr als eine geht nicht.';
    case 'quelle_ist_zugeordnet':
      return 'Diese Bestellung ist bereits zugeordnet. Hebe die Zuordnung erst auf.';
    case 'quelle_traegt_zuordnung':
      return fremdeNr
        ? `An dieser Bestellung hängt selbst ${fremdeNr}. Sie weiterzureichen ergäbe eine Kette — hebe die bestehende Zuordnung erst auf.`
        : 'An dieser Bestellung hängt selbst eine Zuordnung. Sie weiterzureichen ergäbe eine Kette.';
    case 'ziel_nicht_gefunden':
      return 'Diese Bestellung gibt es nicht (mehr).';
    case 'nicht_zugeordnet':
      return 'Diese Bestellung ist gar nicht zugeordnet.';
    default:
      return 'Das hat nicht geklappt.';
  }
}

/** Die Nummer, unter der ein Mensch die Bestellung kennt. */
function anzeigeNr(z: DetailZuordnung): string {
  return z.bestellnr;
}

export function ZuordnungSektion({
  anker,
  bestellung
}: {
  anker?: string;
  bestellung: Bestellung;
}) {
  // `?? null` und nicht `bestellung.zugeordnet_zu` allein: Antwortet
  // der Server ohne das Feld — ältere Fassung im Umlauf, ein Fehlerpfad,
  // ein Prüfstand mit anderer Attrappe —, ist es `undefined`, und ein
  // Vergleich auf `!== null` ginge daran vorbei. Genau das ist beim
  // ersten Lauf passiert: vier fremde Zusicherungen stürzten mit
  // „Cannot read properties of undefined" ab. Ein Abschnitt, der leer
  // bleibt, ist ein Ärgernis; eine Detailseite, die deshalb weiß
  // bleibt, ist ein Ausfall (die Lehre aus A31).
  const zugeordnetZu = bestellung.zugeordnet_zu ?? null;
  const zugeordnete = bestellung.zugeordnete_bestellung ?? null;

  // Vier Lagen, und nur in dreien steht hier überhaupt etwas:
  //   1. Diese Bestellung ist zugeordnet         → Hinweis + Aufheben
  //   2. Ihr ist eine zugeordnet                 → der Beleg-Abschnitt
  //   3. Sie kommt aus Shopify und ist frei      → der Weg zur Zuordnung
  //   4. Ladenbestellung ohne Zuordnung          → nichts. Der Weg
  //      beginnt an der Shopify-Bestellung, dort fällt die Doppelung
  //      auf; an jeder Ladenbestellung stünde er nur im Weg.
  const ausShopify = bestellung.shopify_order_number !== null;
  if (zugeordnetZu === null && zugeordnete === null && !ausShopify) return null;

  return (
    <section data-tour={anker} className="space-y-2">
      <h3 className="font-serif text-base text-ink">Zuordnung</h3>
      {zugeordnetZu !== null && (
        <IstZugeordnet bestellung={bestellung} ziel={zugeordnetZu} />
      )}
      {zugeordnete !== null && <TraegtZuordnung eintrag={zugeordnete} />}
      {zugeordnetZu === null && zugeordnete === null && ausShopify && (
        <Zuordnen bestellung={bestellung} />
      )}
    </section>
  );
}

/** Lage 1: Diese Bestellung gehört zu einer anderen. */
function IstZugeordnet({
  bestellung,
  ziel
}: {
  bestellung: Bestellung;
  ziel: DetailZuordnung;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  async function aufheben(): Promise<void> {
    setLaeuft(true);
    try {
      await apiFetch('/bestellungen/zuordnung-aufheben.php', {
        method: 'POST',
        body: JSON.stringify({ id: bestellung.id })
      });
      // Beide Seiten neu holen — und die Listen, aus denen die
      // Bestellung gerade zurückkehrt.
      await qc.invalidateQueries({ queryKey: ['bestellung'] });
      await qc.invalidateQueries({ queryKey: ['bestellungen'] });
      toast('success', `Zuordnung zu ${anzeigeNr(ziel)} aufgehoben.`);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      toast('error', zuordnungFehlerText(code));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="rounded-md border border-akzent bg-cream-warm p-3">
      <p className="flex flex-wrap items-center gap-1.5 text-sm text-ink">
        <Link2 aria-hidden className="h-4 w-4 shrink-0 text-akzent" />
        <span>Diese Bestellung ist</span>
        <Link
          to="/bestellungen/$bestellnr"
          params={{ bestellnr: ziel.bestellnr }}
          // `touch-ziel` statt echter 44 px: Der Verweis steht MITTEN in
          // einem Satz und ist 20 Pixel hoch; ihn auf 44 zu ziehen
          // risse die Zeile auseinander. Die unsichtbare Flaeche gibt
          // ihm das Beruehrziel, ohne den Satz anzufassen — dieselbe
          // Ueberlegung wie am Zustands-Chip und an der Notiz-Marke.
          className="touch-ziel font-serif text-akzent underline"
        >
          {anzeigeNr(ziel)}
        </Link>
        <span>zugeordnet.</span>
      </p>
      <p className="mt-1 text-xs text-ink-soft">
        Sie steht deshalb in keiner Liste und in keinem Zähler mehr — die Arbeit am
        Buch läuft über {anzeigeNr(ziel)}. Ihre eigenen Angaben, auch Sendung und
        Sendungsnummer, bleiben hier stehen.
      </p>
      <div className="mt-2">
        <Button variant="ghost" onClick={() => void aufheben()} disabled={laeuft}>
          {laeuft ? <Spinner /> : <Link2Off aria-hidden className="h-4 w-4" />}
          Zuordnung aufheben
        </Button>
      </div>
    </div>
  );
}

/**
 * Lage 2: Dieser Bestellung ist eine zugeordnet — schreibgeschützt.
 *
 * Versanddatum und Sendungsnummer sind der Grund für das ganze Paket:
 * Sie hängen an der Shopify-Bestellung, und ohne diesen Abschnitt wären
 * sie mit ihr aus dem Blick verschwunden.
 */
function TraegtZuordnung({ eintrag }: { eintrag: DetailZuordnung }) {
  return (
    <div className="rounded-md border border-line bg-cream p-3">
      <p className="flex flex-wrap items-center gap-1.5 text-sm text-ink">
        <Link2 aria-hidden className="h-4 w-4 shrink-0 text-akzent" />
        <span>Zugeordnete Shopify-Bestellung</span>
        <Link
          to="/bestellungen/$bestellnr"
          params={{ bestellnr: eintrag.bestellnr }}
          className="touch-ziel font-serif text-akzent underline"
        >
          {anzeigeNr(eintrag)}
        </Link>
      </p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-ink-soft">Zustand</dt>
        <dd className="flex items-center gap-1.5 text-ink">
          {/* Die Farbe steht im style: sie kommt aus der Datenbank und
              kann gar keine Klasse sein (Befunde aus A27, A31, A33). */}
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: eintrag.zustand_farbe ?? 'transparent' }}
          />
          {eintrag.zustand_label}
        </dd>
        <dt className="text-ink-soft">Versendet</dt>
        <dd className="text-ink">
          {eintrag.versendet_am === null ? '—' : datumDe(eintrag.versendet_am)}
        </dd>
        <dt className="text-ink-soft">Sendungsnummer</dt>
        <dd className="text-ink">
          {eintrag.sendungsnummer === null ? '—' : eintrag.sendungsnummer}
        </dd>
      </dl>
      <p className="mt-2 text-xs text-ink-soft">
        Beleg, kein Arbeitsvorrat: Positionen und Mengen dort bleiben, wo sie sind.
        Aufheben lässt sich die Zuordnung an der Shopify-Bestellung selbst.
      </p>
    </div>
  );
}

/** Lage 3: eine freie Shopify-Bestellung — der Weg zur Zuordnung. */
function Zuordnen({ bestellung }: { bestellung: Bestellung }) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<BestellungZeile[] | null>(null);
  const [sucht, setSucht] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<BestellungZeile | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  async function suchen(): Promise<void> {
    const q = suche.trim();
    if (q === '') return;
    setSucht(true);
    setGewaehlt(null);
    try {
      // Dieselbe Suche wie im Brett: Nummer, Kunde, ISBN, Titel. Sie
      // liefert von sich aus keine zugeordneten Bestellungen mehr — der
      // Filter steht in dashBuildWhere.
      const res = await apiFetch<BestellungenResponse>(
        `/bestellungen.php?q=${encodeURIComponent(q)}&limit=10`
      );
      setTreffer(res.bestellungen.filter((z) => z.id !== bestellung.id));
    } catch {
      toast('error', 'Die Suche hat nicht geklappt.');
      setTreffer([]);
    } finally {
      setSucht(false);
    }
  }

  async function zuordnen(ziel: BestellungZeile): Promise<void> {
    setLaeuft(true);
    try {
      await apiFetch('/bestellungen/zuordnen.php', {
        method: 'POST',
        body: JSON.stringify({ id: bestellung.id, ziel_id: ziel.id })
      });
      await qc.invalidateQueries({ queryKey: ['bestellung'] });
      await qc.invalidateQueries({ queryKey: ['bestellungen'] });
      toast('success', `${bestellung.bestellnr} ist jetzt ${ziel.bestellnr} zugeordnet.`);
      setOffen(false);
      setGewaehlt(null);
      setTreffer(null);
      setSuche('');
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      const nr = e instanceof ApiError ? (e.daten.fremde_nr as string | undefined) : undefined;
      toast('error', zuordnungFehlerText(code, nr));
    } finally {
      setLaeuft(false);
    }
  }

  if (!offen) {
    return (
      <div>
        <Button variant="secondary" onClick={() => setOffen(true)}>
          <Link2 aria-hidden className="h-4 w-4" />
          Dieser Bestellung zuordnen
        </Button>
        <p className="mt-1 text-xs text-ink-soft">
          Für den Fall, dass dieselbe Ware schon am Tresen erfasst wurde und nur
          nachträglich in Shopify angelegt werden musste.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-akzent bg-cream-warm p-3">
      <p className="text-sm text-ink">
        Welcher Bestellung gehört {bestellung.bestellnr} an? Suche nach Nummer oder
        Kunde.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[12rem] flex-1 text-xs text-ink-soft">
          Nummer oder Kunde
          <input
            type="text"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void suchen();
              }
            }}
            className={`mt-1 ${FELD_KLASSE}`}
          />
        </label>
        <Button variant="secondary" onClick={() => void suchen()} disabled={sucht}>
          {sucht ? <Spinner /> : <Search aria-hidden className="h-4 w-4" />}
          Suchen
        </Button>
      </div>

      {treffer !== null && treffer.length === 0 && (
        <p className="text-sm text-ink-soft">Keine Bestellung gefunden.</p>
      )}

      {treffer !== null && treffer.length > 0 && (
        <ul className="space-y-1">
          {treffer.map((z) => (
            <li key={z.id}>
              <button
                type="button"
                onClick={() => setGewaehlt(z)}
                aria-pressed={gewaehlt?.id === z.id}
                className={`flex min-h-[44px] w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm ${
                  gewaehlt?.id === z.id
                    ? 'border-akzent bg-akzent-sanft'
                    : 'border-line bg-cream'
                }`}
              >
                <span className="font-serif text-akzent">{z.bestellnr}</span>
                <span className="min-w-0 flex-1 truncate text-ink">
                  {z.kunde.anzeigename}
                </span>
                <span className="shrink-0 text-xs text-ink-soft">{z.zustand.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Die Bestätigung sagt, was danach anders ist — und was NICHT.
          „Zusammenlegen" wäre das falsche Wort: Es wird nichts
          verschmolzen, die Bestellung verschwindet nur aus dem Brett. */}
      {gewaehlt !== null && (
        <div className="rounded-md border border-line bg-cream p-3 text-sm">
          <p className="flex flex-wrap items-center gap-1.5 text-ink">
            <span className="font-serif">{bestellung.bestellnr}</span>
            <ArrowRight aria-hidden className="h-4 w-4 text-ink-soft" />
            <span className="font-serif">{gewaehlt.bestellnr}</span>
            <span className="text-ink-soft">({gewaehlt.kunde.anzeigename})</span>
          </p>
          <p className="mt-1 text-xs text-ink-soft">
            {bestellung.bestellnr} verschwindet danach aus Brett, Zählern und
            Wareneingang und ist über {gewaehlt.bestellnr} zu finden. Positionen,
            Mengen und Sendungen bleiben, wo sie sind — es wird nichts
            zusammengelegt. Rückgängig machen lässt sich das jederzeit.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => void zuordnen(gewaehlt)}
              disabled={laeuft}
            >
              {laeuft ? <Spinner /> : null}
              Zuordnen
            </Button>
            <Button variant="ghost" onClick={() => setGewaehlt(null)}>
              Andere wählen
            </Button>
          </div>
        </div>
      )}

      <div>
        <Button
          variant="ghost"
          onClick={() => {
            setOffen(false);
            setGewaehlt(null);
            setTreffer(null);
          }}
        >
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
